import { MultiSelect } from '@/components/multi-select'
import { PhoneInput } from '@/components/phone-input'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useFilters } from '@/hooks/useFilters'
import { listContactsQuery } from '@/queries/contact.query'
import { listTagsQuery } from '@/queries/tags.query'
import { ContactService } from '@/services/contacts.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Contact } from '../schema'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentRow?: Contact
}

const formSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório.'),
  phone: z.string().min(1, 'Telefone é obrigatório.'),
  email: z.string().optional(),
  tagIds: z.array(z.string()),
})
type ContactForm = z.infer<typeof formSchema>

export function ContactsMutateDrawer({ open, onOpenChange, currentRow }: Props) {
  const isUpdate = !!currentRow
  const queryClient = useQueryClient();
  const { data: tagsData } = useSuspenseQuery(listTagsQuery)
  const { filters } = useFilters('/_authenticated/contacts/')

  const form = useForm<ContactForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: currentRow?.name ?? '',
      phone: currentRow?.phone ? `+${currentRow.phone}` : '',
      email: currentRow?.email ?? '',
      tagIds: currentRow?.tags.map((tag) => String(tag.id)) ?? [],
    },
  })

  const onSubmit = form.handleSubmit((data: ContactForm) => {
    const payload = {
      ...data,
      name: data.name.trim(),
      phone: data.phone.replace(/\D/g, ''),
      tagIds: data.tagIds.map(Number),
    }

    const mutation = isUpdate && currentRow
      ? ContactService.update(currentRow.id, payload)
      : ContactService.create(payload)

    toast.promise(mutation, {
      loading: 'Salvando contato...',
      success: () => {
        onOpenChange(false)
        form.reset()
        queryClient.invalidateQueries({ queryKey: listContactsQuery(filters).queryKey })
        return isUpdate ? 'Contato atualizado' : 'Contato criado'
      },
      error: () => {
        return isUpdate ? 'Ocorreu um erro ao tentar atualizar o contato.' : 'Ocorreu um erro ao tentar criar o contato.'
      },
    })
  })

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        form.reset()
      }}
    >
      <SheetContent className='flex flex-col'>
        <SheetHeader className='text-left'>
          <SheetTitle>{isUpdate ? 'Atualizar' : 'Adicionar'} Contato</SheetTitle>
          <SheetDescription>
            {isUpdate
              ? 'Atualize o contato provendo as informações necessárias.'
              : 'Adicione um novo contato provendo as informações necessárias.'}
            Clique em salvar quando estiver pronto.
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            id='contacts-form'
            onSubmit={onSubmit}
            className='flex-1 space-y-5'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus placeholder='Digite o nome do contato' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      defaultCountry="BR"
                      placeholder="Digite o telefone do contato"
                      initialValueFormat='national'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Digite o email do contato' type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='tagIds'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Segmentos</FormLabel>
                  <MultiSelect
                    placeholder="Selecione os segmentos"
                    options={tagsData.map(tag => ({
                      label: tag.name,
                      value: tag.id.toString()
                    }))}
                    onValueChange={field.onChange}
                    defaultValue={field.value || []}
                    value={field.value || []}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <SheetFooter className='gap-2'>
          <SheetClose asChild>
            <Button variant='outline'>Cancelar</Button>
          </SheetClose>
          <Button form='contacts-form' type='submit'>
            {isUpdate ? 'Atualizar' : 'Adicionar'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
